import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/productrestrictions';

const DOC_TYPE_OPTIONS = [
  { value: '', label: 'Hepsi' },
  { value: 1, label: 'Sipariş' },
];
const docTypeLabel = (value) => {
  if (value === null || value === undefined || value === '') return 'Hepsi';
  const found = DOC_TYPE_OPTIONS.find((o) => o.value === value);
  return found ? found.label : String(value);
};

const EMPTY_FORM = {
  id: '', textId: '', description: '', documentType: '',
  customerGroupId: '', customerSubGroupId: '', customerId: '', isActive: true,
};

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

export function ProductRestrictionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Ürün Kısıtlaması' : 'Ürün Kısıtlaması Detayı', 'Belge tipi ve müşteri kriterleri bazında ürünleri satışa kapatın.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Child-grid: satışa kapalı ürünler
  const [products, setProducts] = useState([]); // { id, productId, label }
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);
  const [productModal, setProductModal] = useState(null); // {} when open
  const [pendingProduct, setPendingProduct] = useState(null); // { id, label }

  // Lookup'lar
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({}); // id -> label (lazy)

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Müşteri grubu / ek grubu lookup'larını yükle
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
      fetch('/api/customersubgroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
    ]).then(([cg, csg]) => {
      setCustomerGroups(unwrapLookupItems(cg));
      setCustomerSubGroups(unwrapLookupItems(csg));
    }).catch(() => {});
  }, []);

  const customerName = useCallback((cid) => (cid ? (customerLabels[cid] || '…') : ''), [customerLabels]);

  // Müşteri etiketlerini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((cid) => cid && customerLabels[cid] === undefined && !custFetchRef.current.has(cid)))];
    if (!need.length) return;
    need.forEach((cid) => custFetchRef.current.add(cid));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((cid) => `Id eq ${cid}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = lookupLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((cid) => { next[cid] = found[cid] || '-'; }); return next; });
      }
    } finally {
      need.forEach((cid) => custFetchRef.current.delete(cid));
    }
  }, [customerLabels]);

  // Tek kayıt yükleme: önce location.state.row ile başlık, sonra GetById ile detay + kriter etiketleri.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    const row = location.state?.row;
    if (row) {
      setForm({
        id: row.id, textId: row.textId || '', description: row.description || '',
        documentType: row.documentType ?? '', customerGroupId: row.customerGroupId || '',
        customerSubGroupId: row.customerSubGroupId || '', customerId: row.customerId || '',
        isActive: row.isActive !== false,
      });
      if (row.customerId) ensureCustomerLabels([row.customerId]);
    }
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Detay alınamadı');
      const payload = body?.data ?? body?.Data ?? {};
      const header = payload.header || payload.Header || {};
      setForm({
        id: header.id ?? header.Id ?? id,
        textId: header.textId ?? header.TextId ?? '',
        description: header.description ?? header.Description ?? '',
        documentType: header.documentType ?? header.DocumentType ?? '',
        customerGroupId: header.customerGroupId ?? header.CustomerGroupId ?? '',
        customerSubGroupId: header.customerSubGroupId ?? header.CustomerSubGroupId ?? '',
        customerId: header.customerId ?? header.CustomerId ?? '',
        isActive: (header.isActive ?? header.IsActive) !== false,
      });
      setAudit({ createdBy: header.createdBy ?? header.CreatedBy ?? header.createdUser ?? header.CreatedUser, createdDate: header.createdDate ?? header.CreatedDate, updatedBy: header.updatedBy ?? header.UpdatedBy ?? header.modifiedUser ?? header.ModifiedUser, updatedDate: header.updatedDate ?? header.UpdatedDate ?? header.modifiedDate ?? header.ModifiedDate });
      const custId = header.customerId ?? header.CustomerId;
      const custLabel = payload.customerLabel ?? payload.CustomerLabel;
      if (custId && custLabel) setCustomerLabels((prev) => ({ ...prev, [custId]: custLabel }));
      else if (custId) ensureCustomerLabels([custId]);
      const detailList = (payload.details || payload.Details || []).map((d) => ({
        id: d.id ?? d.Id, productId: d.productId ?? d.ProductId ?? null, label: d.label ?? d.Label ?? '',
      }));
      setProducts(detailList);
      setDeletedDetailIds([]);
    } catch (error) {
      showToast('error', error.message || 'Detay alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /product-restrictions/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      const c = location.state.clone;
      setForm({ ...c, id: '' });
      setProducts((c.products || []).map((p) => ({ id: '', productId: p.productId, label: p.label })));
      setDeletedDetailIds([]);
      setTried(false);
      const custId = c.customerId;
      if (custId && c.customerLabel) setCustomerLabels((prev) => ({ ...prev, [custId]: c.customerLabel }));
      else if (custId) ensureCustomerLabels([custId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── Child-grid: ürün ekle/çıkar ──
  const openAddProduct = () => { setPendingProduct(null); setProductModal({}); };
  const confirmAddProduct = () => {
    if (!pendingProduct?.id) { showToast('error', 'Bir ürün seçiniz'); return; }
    if (products.some((p) => p.productId === pendingProduct.id)) { showToast('error', 'Bu ürün zaten eklenmiş'); return; }
    setProducts((current) => [...current, { id: '', productId: pendingProduct.id, label: pendingProduct.label }]);
    setPendingProduct(null);
    setProductModal(null);
  };
  const removeProduct = (index) => {
    setProducts((current) => {
      const target = current[index];
      if (target?.id) setDeletedDetailIds((ids) => (ids.includes(target.id) ? ids : [...ids, target.id]));
      return current.filter((_, i) => i !== index);
    });
  };

  const buildDetailPayload = () => products.map((p) => ({ id: p.id || '00000000-0000-0000-0000-000000000000', productId: p.productId }));

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan. Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'Kod boş olamaz'); return; }
    setSaving(true);
    try {
      const docType = form.documentType === '' || form.documentType === null ? null : Number(form.documentType);
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          header: {
            id: form.id || '00000000-0000-0000-0000-000000000000', textId: form.textId, description: form.description,
            documentType: docType, customerGroupId: form.customerGroupId || null, customerSubGroupId: form.customerSubGroupId || null,
            customerId: form.customerId || null, isActive: !!form.isActive,
          },
          details: buildDetailPayload(),
          deletedDetailIds,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt kaydedilemedi');
      if (isNew) {
        if (andNew) {
          showToast('success', 'Ürün kısıtlaması eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setProducts([]);
          setDeletedDetailIds([]);
          setPendingProduct(null);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Ürün kısıtlaması başarıyla kaydedildi');
        setTimeout(() => navigate('/product-restrictions'), 400);
        return;
      }
      showToast('success', 'Ürün kısıtlaması başarıyla kaydedildi');
      setTimeout(() => navigate('/product-restrictions'), 400);
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form + ürün bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/product-restrictions/new', {
      state: {
        clone: {
          ...form,
          id: '',
          products: products.map((p) => ({ productId: p.productId, label: p.label })),
          customerLabel: form.customerId ? customerLabels[form.customerId] : undefined,
        },
      },
    });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: form.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Ürün kısıtlaması başarıyla silindi');
      setTimeout(() => navigate('/product-restrictions'), 400);
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (form.isActive
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="bad">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/product-restrictions')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni kısıtlama aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  const customerGroupSelectedLabel = form.customerGroupId
    ? lookupLabel(customerGroups.find((g) => g.id === form.customerGroupId)) : '';
  const customerSubGroupSelectedLabel = form.customerSubGroupId
    ? lookupLabel(customerSubGroups.find((g) => g.id === form.customerSubGroupId)) : '';

  return (
    <DetailShell
      crumb={[{ label: 'Ürün Kısıtlama', to: '/product-restrictions' }, { label: isNew ? 'Yeni Kısıtlama' : 'Kısıtlama Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'ProductRestriction', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="shield" title="Kısıtlama Bilgileri" subtitle="Kod, belge tipi ve aktiflik durumu">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
            <FieldText form={form} set={set} k="description" label="Açıklama" />
            <FieldSelect
              form={{ ...form, documentType: form.documentType === null ? '' : form.documentType }}
              set={(k, v) => set(k, v === '' ? '' : Number(v))}
              k="documentType" label="Belge Tipi" options={DOC_TYPE_OPTIONS}
            />
            <FieldSelect
              form={{ ...form, isActive: form.isActive ? 'true' : 'false' }}
              set={(k, v) => set(k, v === 'true')}
              k="isActive" label="Aktiflik"
              options={[{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }]}
            />
          </Grid>
        </Section>

        <Section icon="users" title="Müşteri Kriterleri" subtitle="Boş bırakılırsa tüm müşterilere uygulanır">
          <Grid cols={3}>
            <FieldCustom label="Müşteri Grubu">
              <RelationPicker
                config={RELATION_CONFIGS.customerGroup} rows={customerGroups}
                value={form.customerGroupId || ''}
                selectedLabel={customerGroupSelectedLabel}
                onChange={(val) => set('customerGroupId', val)}
                placeholder="Tümü"
              />
            </FieldCustom>
            <FieldCustom label="Müşteri Ek Grubu">
              <RelationPicker
                config={RELATION_CONFIGS.customerSubGroup} rows={customerSubGroups}
                value={form.customerSubGroupId || ''}
                selectedLabel={customerSubGroupSelectedLabel}
                onChange={(val) => set('customerSubGroupId', val)}
                placeholder="Tümü"
              />
            </FieldCustom>
            <FieldCustom label="Müşteri">
              <RelationPicker
                config={RELATION_CONFIGS.customer}
                value={form.customerId || ''}
                selectedLabel={form.customerId ? customerName(form.customerId) : ''}
                onChange={(val, item) => {
                  set('customerId', val);
                  if (val && item) setCustomerLabels((prev) => ({ ...prev, [val]: lookupLabel(item) }));
                }}
                placeholder="Tümü"
              />
            </FieldCustom>
          </Grid>
        </Section>

        <Section
          icon="box"
          title="Satışa Kapalı Ürünler"
          subtitle="Bu kriterlere uyan belgelerde seçilen ürünler satışa kapatılır"
          right={<button className="dps-blk-add" onClick={openAddProduct}><Icon name="plus" size={15} /> Ürün Ekle</button>}
        >
          {products.length === 0 ? (
            <div className="dps-empty"><Icon name="box" size={34} /><p>Henüz ürün eklenmedi</p><span>Sağ üstten “Ürün Ekle” ile ürün seçin.</span></div>
          ) : (
            <table className="dps-table">
              <thead>
                <tr><th>Ürün</th><th className="dps-td-actions">İşlem</th></tr>
              </thead>
              <tbody>
                {products.map((p, index) => (
                  <tr key={p.id || `${p.productId}-${index}`}>
                    <td><strong>{p.label || '(ürün)'}</strong></td>
                    <td className="dps-td-actions">
                      <button className="dps-row-btn danger" onClick={() => removeProduct(index)}>Çıkar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {/* Ürün ekleme modalı */}
      {productModal && (
        <SubModal
          title="Ürün Ekle"
          onClose={() => { setProductModal(null); setPendingProduct(null); }}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => { setProductModal(null); setPendingProduct(null); }}>İptal</button>
              <button className="dps-btn primary" onClick={confirmAddProduct}>Ekle</button>
            </>
          )}
        >
          <FieldCustom label="Ürün (kod/ad ara)">
            <RelationPicker
              config={RELATION_CONFIGS.product}
              value={pendingProduct?.id || ''}
              selectedLabel={pendingProduct?.label}
              onChange={(val, item) => setPendingProduct(val ? { id: val, label: item ? lookupLabel(item) : '' } : null)}
            />
          </FieldCustom>
        </SubModal>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.textId}”</strong> kısıtlamasını silmek istediğinize emin misiniz? Bağlı tüm ürün satırları da silinecek.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default ProductRestrictionDetailPage;
